import React from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Button,
  Alert,
  ProgressStepper,
  Step,
  StepButton,
  StepLabel,
} from '@one-thd/sui-atomic-components';
import { SweepstakesDrawer } from './SweepstakesDrawer';
import { SweepstakesMarkdown } from '../SweepstakesMarkdown';
import { SWEEPSTAKES_CONSTANTS } from '../../../core/Constants';
import { SWEEPSTAKES_DETAILS_CONTENT } from '../../../core/Data/SweepstakesMockData';

export const SweepstakesDetailsDrawer = ({
  isOpen,
  onClose,
  onStepClick,
  sweepstakesData,
  isLoading,
  hasError,
}) => {
  const handleStepClick = (step) => {
    if (onStepClick) {
      onStepClick(step);
    }
  };

  const footerContent = sweepstakesData?.ctaTitle && (
    <Button
      variant="primary"
      fullWidth
      data-testid="sweepstakes-details-cta-button"
    >
      {sweepstakesData.ctaTitle}
    </Button>
  );

  return (
    <SweepstakesDrawer
      isOpen={isOpen}
      onClose={onClose}
      drawerTitle={SWEEPSTAKES_CONSTANTS.DRAWER_TITLES.SWEEPSTAKES_DETAILS}
      sweepstakesTitle={sweepstakesData?.title}
      logoUrl={sweepstakesData?.logo}
      footerContent={footerContent}
      isLoading={isLoading}
      hasError={hasError}
      testId="sweepstakes-details-drawer"
    >
      {sweepstakesData && (
        <div className="sui-space-y-6">
          <div>
            <SweepstakesMarkdown markdownText={SWEEPSTAKES_DETAILS_CONTENT.initialParagraph} />
          </div>
          {sweepstakesData.steps && sweepstakesData.steps.length > 0 && (
            <div>
              <div className="sui-mb-4">
                <Typography variant="body-lg" weight="bold">
                  {SWEEPSTAKES_DETAILS_CONTENT.stepsHeader}
                </Typography>
              </div>
              <ProgressStepper
                centerEdgeNodes
                nonLinear
                orientation="vertical"
                connector={<></>}
              >
                {sweepstakesData.steps.map((step, index) => (
                  <Step key={step.id} completed={step.completed}>
                    <StepButton
                      onClick={() => handleStepClick(step)}
                      icon={() => (
                        <img
                          src={step.iconUrl}
                          alt={`Step ${index + 1}`}
                          width={32}
                          height={32}
                          className="sui-rounded-full"
                        />
                      )}
                    />
                    <StepLabel>
                      <div>
                        <Typography variant="body-base" decoration="underline">
                          {step.title}
                        </Typography>
                        {step.subtitle && (
                          <div className="sui-mt-1">
                            <Typography variant="body-sm" color="subtle">
                              {step.subtitle}
                            </Typography>
                          </div>
                        )}
                      </div>
                    </StepLabel>
                  </Step>
                ))}
              </ProgressStepper>
            </div>
          )}
          {sweepstakesData.expirationDate && (
            <div>
              <Alert status="info" size="large">
                <Typography variant="body-base">Expires {sweepstakesData.expirationDate}</Typography>
              </Alert>
            </div>
          )}
          {sweepstakesData.termsAndConditions && (
            <div>
              <SweepstakesMarkdown markdownText={sweepstakesData.termsAndConditions} />
            </div>
          )}
        </div>
      )}
    </SweepstakesDrawer>
  );
};

SweepstakesDetailsDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onStepClick: PropTypes.func,
  sweepstakesData: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    logo: PropTypes.string,
    expirationDate: PropTypes.string,
    termsAndConditions: PropTypes.string,
    steps: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      subtitle: PropTypes.string,
      iconUrl: PropTypes.string,
      completed: PropTypes.bool,
    })),
    completedSteps: PropTypes.number,
    ctaTitle: PropTypes.string,
  }),
  isLoading: PropTypes.bool,
  hasError: PropTypes.bool,
};

SweepstakesDetailsDrawer.defaultProps = {
  onStepClick: null,
  sweepstakesData: null,
  isLoading: false,
  hasError: false,
};